import { IBreadcrumbItem, PivotItem } from '@fluentui/react';
import { Button, buttonStylesPrimary, Pivot, useHaicPageTitle } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import semver from 'semver';

import { workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { HeaderPure } from '../../components/Header/Header';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { DeploymentFixed } from '../../mlops/api';
import { DeploymentStatus_DeploymentState } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_status_pb';
import {
  useQueryDeployment,
  useQueryDeploymentMetadata,
  useQueryDeploymentStatus,
  useQueryMonitoringConfig,
} from '../../mlops/mlops.deployer.hooks';
import { getSecurityKey, SecurityKey } from '../../mlops/utils';
import { GrpcStatusCode, isGrpcGatewayErrorResponse, TwirpErrorCode } from '../../services/HTTPResponse';
import { useEnv } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import ErrorPage from '../ErrorPage';
// TODO: Move to global context.
import { fillStyles, LoaderView } from '../Orchestrator/WorkflowDetail';
import { DeploymentTabDetail, getDeploymentType, IDeploymentTabDetailProps } from './DeploymentTabDetail';
import { DeploymentTabEndpoints, IDeploymentTabEndpointsProps } from './DeploymentTabEndpoints';
import { DeploymentTabMonitoring, IDeploymentTabMonitoringProps } from './DeploymentTabMonitoring';
import { DeploymentTabScoring, IDeploymentTabScoringProps } from './DeploymentTabScoring';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';
import { useGranularMLOpsPermissions } from './userActions';

export type DeploymentDetailParams = { deployment_id: string; workspace_id: string; tab_id: string; item_name: string };

/**
 * This enum gives a name to each tab in the pivot component. The values of the enum are indexes of the tabs.
 */
export enum DeploymentTab {
  DETAILS = 0,
  ENDPOINTS,
  SCORING,
  MONITORING,
}

export type DeploymentDetailContentProps = {
  detailTabProps: IDeploymentTabDetailProps;
  endpointsTabProps: IDeploymentTabEndpointsProps;
  loading?: boolean;
  monitoringTabProps?: IDeploymentTabMonitoringProps; // Optional, only if monitoring is enabled.
  onTabClick: (tab: DeploymentTab) => void;
  scoringTabProps: IDeploymentTabScoringProps;
  selectedTab: DeploymentTab;
  statusUnhealthy?: boolean;
  workspaceId: string;
};

export const DeploymentDetailContent = (props: DeploymentDetailContentProps) => {
  const { loading, monitoringTabProps, onTabClick, statusUnhealthy } = props,
    workspaceName = React.useMemo(() => `workspaces/${props.workspaceId}`, [props.workspaceId]),
    permissions = useGranularMLOpsPermissions(workspaceName),
    onItemClick = React.useCallback(
      (item?: PivotItem) => {
        if (!item) return;
        onTabClick(Number(item.props.itemKey) as DeploymentTab);
      },
      [onTabClick]
    ),
    unhealthyElement = !statusUnhealthy
      ? null
      : NoItemView({
          title: 'Deployment is not ready',
          description: 'To access this page, deployment must be in the healthy state.',
        });
  return (
    <Pivot
      {...fillStyles}
      onLinkClick={onItemClick}
      selectedKey={String(props.selectedTab || 0)}
      placeholder={undefined}
      items={[
        ...(permissions.hasAccessToDeploymentDetailPage_create || permissions.hasAccessToDeploymentDetailPage_view
          ? [
              {
                headerText: 'Details',
                content: loading ? (
                  <LoaderView loaderText="Loading deployment detail..." />
                ) : (
                  <DeploymentTabDetail {...props.detailTabProps} />
                ),
              },
            ]
          : []),
        ...(permissions.hasAccessToDeploymentEndpointsTab
          ? [
              {
                headerText: 'Endpoints',
                content:
                  unhealthyElement ||
                  (loading ? (
                    <LoaderView loaderText="Loading endpoints..." />
                  ) : (
                    <DeploymentTabEndpoints {...props.endpointsTabProps} />
                  )),
              },
            ]
          : []),
        ...(permissions.hasAccessToDeploymentQuickScoreTab
          ? [
              {
                headerText: 'Quick scoring',
                content:
                  unhealthyElement ||
                  (loading ? (
                    <LoaderView loaderText="Loading scoring UI..." />
                  ) : (
                    <DeploymentTabScoring {...props.scoringTabProps} />
                  )),
              },
            ]
          : []),
        ...(monitoringTabProps && permissions.hasAccessToDeploymentMonitoringTab
          ? [
              {
                headerText: 'Monitoring',
                content:
                  unhealthyElement ||
                  (loading ? (
                    <LoaderView loaderText="Loading monitoring UI..." />
                  ) : (
                    <DeploymentTabMonitoring {...monitoringTabProps} />
                  )),
              },
            ]
          : []),
      ]}
    />
  );
};

export const MLOpsError404Page = (returnTo: string, actionButtonText: string) => (
  <ErrorPage
    pageTitle="404 Error"
    code={TwirpErrorCode.NotFound}
    message="Sorry, we can't seem to find what you are looking for."
    imageSrc="404.svg"
    actionButtons={<Button styles={buttonStylesPrimary} text={actionButtonText} href={returnTo} />}
  />
);

const isGrpcGatewayError404 = (err: unknown): boolean =>
  isGrpcGatewayErrorResponse(err) &&
  (err.code === GrpcStatusCode.NOT_FOUND ||
    err.code === GrpcStatusCode.INVALID_ARGUMENT ||
    err.code === GrpcStatusCode.INTERNAL);

/**
 * The current pivot tab is stored in the URL, this maps the path parameter to the corresponding DeploymentTab enum value.
 */
const pathTabToDeploymentTab = {
  '': DeploymentTab.DETAILS,
  endpoints: DeploymentTab.ENDPOINTS,
  scoring: DeploymentTab.SCORING,
  monitoring: DeploymentTab.MONITORING,
} as const;

const DeploymentDetail = () => {
  const params = useParams<DeploymentDetailParams>();
  const workspaceFQDN = React.useMemo(
    () => workspaceName.compile({ workspace: params.workspace_id }),
    [params.workspace_id]
  );
  const isNew = params.deployment_id === 'create-new';
  const permissions = useGranularMLOpsPermissions(workspaceFQDN);

  const deploymentQuery = useQueryDeployment(
    {
      deploymentId: params.deployment_id,
      workspaceId: params.workspace_id,
    },
    {
      enabled: Boolean(permissions?.isAbleToViewDeployment && !isNew),
      meta: {
        toastError: (err: unknown) =>
          isGrpcGatewayError404(err) ? undefined : `Failed to fetch deployment: ${formatError(err)}`,
      },
    }
  );
  const deployment = deploymentQuery.data as DeploymentFixed | undefined;
  const showNotFound = React.useMemo<boolean>(
    () => isGrpcGatewayError404(deploymentQuery.error),
    [deploymentQuery.error]
  );

  const deploymentMetadataQuery = useQueryDeploymentMetadata(
    {
      deploymentId: params.deployment_id,
      workspaceId: params.workspace_id,
    },
    {
      enabled: Boolean(permissions?.isAbleToViewDeployment && !isNew),
      meta: {
        toastError: (err: unknown) =>
          isGrpcGatewayError404(err) ? undefined : `Failed to fetch deployment metadata: ${formatError(err)}`,
      },
    }
  );
  const scorerEndpoint = deploymentMetadataQuery.data?.scoringInterface?.score?.uri || ``;
  const sampleRequestUrl = deploymentMetadataQuery.data?.scoringInterface?.sampleRequest?.uri || ``;

  const deploymentStatusQuery = useQueryDeploymentStatus(
    {
      deploymentId: params.deployment_id,
      workspaceId: params.workspace_id,
    },
    {
      enabled: Boolean(permissions?.isAbleToViewDeployment && !isNew),
      meta: {
        toastError: (err: unknown) =>
          isGrpcGatewayError404(err) ? undefined : `Failed to fetch deployment status: ${formatError(err)}`,
      },
    }
  );
  const deploymentStatus = deploymentStatusQuery.data;
  const monitoringConfigQuery = useQueryMonitoringConfig({
    meta: {
      toastError: (err: unknown) => `Failed to fetch monitoring config: ${formatError(err)}`,
    },
  });
  const monitoringConfig = monitoringConfigQuery.data?.monitoringConfig;
  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');

  const history = useHistory(),
    env = useEnv(),
    isDeployModel = React.useMemo(
      () => params.tab_id === 'deploy' && params.item_name,
      [params.tab_id, params.item_name]
    ),
    [defaultSelectedSecurityOptionKey, setDefaultSelectedSecurityOptionKey] = React.useState<SecurityKey | undefined>(),
    [defaultScoringPassphrase, setDefaultScoringPassphrase] = React.useState<string>(),
    monitoringGloballyDisabled = !monitoringConfig?.enabled,
    isNotHealthy =
      !isNew &&
      !(
        deploymentStatus?.state === DeploymentStatus_DeploymentState.HEALTHY ||
        // In case of STOPPED deployment, tabs should be enabled, only Quick Scoring shouldn't be allowed.
        deploymentStatus?.state === DeploymentStatus_DeploymentState.STOPPED
      ),
    updateDeleteAllowed =
      isNew ||
      (isMlopsApiV1_0_x
        ? deploymentStatus?.state === DeploymentStatus_DeploymentState.HEALTHY ||
          deploymentStatus?.state === DeploymentStatus_DeploymentState.FAILED ||
          deploymentStatus?.state === DeploymentStatus_DeploymentState.UNHEALTHY ||
          deploymentStatus?.state === DeploymentStatus_DeploymentState.STOPPED
        : deploymentStatus?.updatesAllowed ?? false),
    breadcrumbs: IBreadcrumbItem[] = [
      {
        key: `home`,
        text: `home`,
        onClick: () => history.push('/'),
      },
      {
        key: `mlops`,
        text: `MLOps`,
        onClick: () => history.push('/mlops'),
      },
      {
        key: 'deployments',
        text: 'Deployments',
        onClick: () => history.push(`/mlops/${workspaceFQDN}/deployments`),
      },
      {
        key: 'deployment',
        text: isNew ? 'Create new deployment' : deployment?.displayName || 'Deployment detail',
        isCurrentItem: true,
      },
    ],
    detailTabProps: IDeploymentTabDetailProps = {
      deployment,
      deploymentStatus,
      defaultSelectedSecurityOptionKey,
      deployModelVersionId: isDeployModel ? params.item_name : undefined,
      isNew,
      updateDeleteAllowed,
      isMonitoringEnabledGlobally: !monitoringGloballyDisabled,
      workspaceId: params.workspace_id,
    },
    endpointsTabProps: IDeploymentTabEndpointsProps = {
      isNew,
      scorerEndpoint,
      deploymentId: deployment?.name?.split('/').pop() || '',
      workspaceId: params.workspace_id,
    },
    scoringTabProps: IDeploymentTabScoringProps = {
      defaultSelectedSecurityOptionKey,
      scorerEndpoint,
      sampleRequestUrl,
      plainTextPassphrase: defaultScoringPassphrase,
      isNew,
      hasNoReplicas:
        deployment?.single?.kubernetesResourceSpec?.replicas === 0 ||
        deployment?.shadow?.primaryElement?.kubernetesResourceSpec?.replicas === 0 ||
        deployment?.shadow?.secondaryElement?.kubernetesResourceSpec?.replicas === 0 ||
        deployment?.split?.splitElements?.[0]?.kubernetesResourceSpec?.replicas === 0 ||
        deployment?.split?.splitElements?.[1]?.kubernetesResourceSpec?.replicas === 0 ||
        deploymentStatus?.state === DeploymentStatus_DeploymentState.STOPPED,
      workspaceId: params.workspace_id,
    },
    monitoringTabProps: IDeploymentTabMonitoringProps | undefined =
      monitoringGloballyDisabled || !deployment?.monitoringOptions?.enabled
        ? undefined
        : {
            deploymentId: params.deployment_id,
            /*
             * For deployments with 2 models, the first model's experiment ID is used.
             * Note, the same logic is used when setting monitoring options during deployment creation.
             * If the deployment creation logic is changed, this needs to be updated as well.
             * Alternatively, we can obtain the experiment ID from the deployment monitoring metadata.
             */
            deploymentType: getDeploymentType(deployment),
            experimentId:
              deployment?.single?.deploymentComposition?.experimentId ||
              deployment?.shadow?.primaryElement?.deploymentComposition?.experimentId ||
              deployment?.split?.splitElements?.[0]?.deploymentComposition?.experimentId,
            secondaryExperimentId: deployment?.single?.deploymentComposition?.experimentId
              ? undefined
              : deployment?.shadow?.secondaryElement?.deploymentComposition?.experimentId ||
                deployment?.split?.splitElements?.[1]?.deploymentComposition?.experimentId,
            frontendUri: monitoringConfig?.frontendUri,
            columnNames: deployment?.monitoringOptions?.columns?.map((col) => col.column),
            deploymentCreationTime: deployment?.createTime ? new Date(deployment?.createTime) : undefined,
            workspaceId: params.workspace_id,
          };

  React.useEffect(() => {
    if (workspaceFQDN && deployment && deploymentStatus) {
      const securityKey = getSecurityKey(deployment);
      if (!defaultSelectedSecurityOptionKey) setDefaultSelectedSecurityOptionKey(securityKey);
      if (
        !defaultScoringPassphrase &&
        deployment.security &&
        `passphrase` in deployment.security &&
        deployment.security.passphrase!.passphrase
      ) {
        setDefaultScoringPassphrase(deployment.security.passphrase?.passphrase);
      }
    }
  }, [deployment, deploymentStatus, workspaceFQDN]);

  const selectedTab = pathTabToDeploymentTab[(params.tab_id || ``) as keyof typeof pathTabToDeploymentTab];
  const onTabClick = React.useCallback(
    (tab: DeploymentTab) => {
      const tabWithRedirect =
        tab === DeploymentTab.MONITORING && (monitoringGloballyDisabled || !deployment?.monitoringOptions?.enabled)
          ? DeploymentTab.DETAILS
          : tab; // If monitoring is not enabled, redirect to details tab.
      const tabPath = Object.keys(pathTabToDeploymentTab).find(
        (key) => pathTabToDeploymentTab[key as keyof typeof pathTabToDeploymentTab] === tabWithRedirect
      );
      history.push(`/mlops/workspaces/${params.workspace_id}/deployments/${params.deployment_id}/${tabPath || ''}`);
    },
    [
      !deployment?.monitoringOptions?.enabled,
      history,
      monitoringGloballyDisabled,
      params.deployment_id,
      params.workspace_id,
    ]
  );

  useHaicPageTitle(
    `${isNew ? 'Create Deployment' : deployment?.displayName || 'Deployment Details'} | MLOps`,
    env?.cloudInstanceName
  );

  if (showNotFound) {
    return MLOpsError404Page(`/mlops/workspaces/${params.workspace_id}/deployments`, 'Return to all deployments');
  }

  const loading =
    !isNew && (deploymentQuery.isLoading || deploymentStatusQuery.isLoading || monitoringConfigQuery.isLoading);

  return (
    <NavigationWrapper>
      <PageWrapper>
        <HeaderPure
          items={breadcrumbs}
          pageTitle={isNew ? 'Create new deployment' : deployment?.displayName || 'Deployment detail'}
        />
        {isNew ? (
          loading ? (
            <LoaderView loaderText="Loading..." />
          ) : (
            <DeploymentTabDetail {...detailTabProps} />
          )
        ) : (
          <DeploymentDetailContent
            detailTabProps={detailTabProps}
            endpointsTabProps={endpointsTabProps}
            monitoringTabProps={monitoringTabProps}
            onTabClick={onTabClick}
            scoringTabProps={scoringTabProps}
            selectedTab={selectedTab}
            statusUnhealthy={isNotHealthy}
            workspaceId={params.workspace_id}
          />
        )}
      </PageWrapper>
    </NavigationWrapper>
  );
};

export default DeploymentDetail;
